import React, { useState } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Container,
  Button,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Home as HomeIcon, Add as AddIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { DeliverableTemplate } from '@horwath-htl/shared-types';
import { TemplateUploadDialog } from '../../components/deliverables/TemplateUploadDialog';

// Custom empty state overlay for the DataGrid
const NoTemplatesOverlay: React.FC<{ onUploadClick: () => void }> = ({ onUploadClick }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    height="100%"
    p={4}
  >
    <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
    <Typography variant="h6" color="text.secondary" gutterBottom>
      No templates found
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
      Upload your first template to get started with document generation.
    </Typography>
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={onUploadClick}
    >
      Upload Template
    </Button>
  </Box>
);

export const DeliverablesTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Fetch templates
  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/deliverables/templates');
      if (!response?.ok) throw new Error('Failed to fetch templates');
      return await response.json();
    },
  });

  const templates: DeliverableTemplate[] = data?.templates || [];

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Template Name',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => {
        const typeColors: Record<string, 'primary' | 'secondary' | 'info'> = {
          global: 'primary',
          personal: 'secondary',
          shared: 'info',
        };
        return (
          <Chip
            label={params.value}
            color={typeColors[params.value] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'language_code',
      headerName: 'Language',
      width: 100,
    },
    {
      field: 'version',
      headerName: 'Version',
      width: 100,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => {
        return new Date(params.value).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      },
    },
  ];

  const handleRowClick = (params: any) => {
    navigate(`/admin/deliverables/templates/${params.row.id}`);
  };

  const handleUploadSuccess = (templateId: string) => {
    navigate(`/admin/deliverables/templates/${templateId}`);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Templates</Typography>
        </Breadcrumbs>

        {/* Header with Action Button */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" component="h1">
            Deliverable Templates
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Add New Template
          </Button>
        </Box>

        {/* Templates DataGrid */}
        <Paper elevation={1} sx={{ height: 600 }}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={templates}
              columns={columns}
              loading={isLoading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: () => <NoTemplatesOverlay onUploadClick={() => setUploadDialogOpen(true)} />,
              }}
              sx={{
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer',
                },
              }}
            />
          )}
        </Paper>
      </Box>

      {/* Upload Dialog */}
      <TemplateUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </Container>
  );
};
